import React, { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useClickOutside } from '../../lib/hooks/useOnClickOutside';
import SignOutIcon from './assets/sign-out.svg';
import HelpIcon from './assets/help.svg';
import BellIcon from './assets/bell.svg';
import { SettingsMenu } from './components/SettingsMenu';
import { NavLinks } from './components/NavLinks';
import { User } from './components/User';
import { NamedContentEntryText } from '../ContentEntryText/NamedContentEntryText';
import { UserAvatar } from './components/UserAvatar';
import { SettingsButton } from './components/SettingsButton';
import { useNavigate } from 'react-router';
import { MobileTopBarMenu } from './components/MobileTopBarMenu';
import { Icon, IconType } from '../Props/Icon/Icon';
import { SwitchViewMenu, ViewOptions } from './components/SwitchViewMenu';

export type Page = {
  name: React.ReactNode;
  path: string;
};

export type TopBarUIUser = {
  name: string | null;
  role: 'learner' | 'manager' | null;
  /**
   * this is a RAW base64 encoded binary blob with content type image/png, not a data URL
   */
  avatar: string | null;
};

export type TopBarUIProps = {
  pages: Page[];
  user: TopBarUIUser;
  logoUrl: string;
  onSignOut: () => void;
  managerView?: boolean;
};
export const TopBarUI: React.FC<TopBarUIProps> = ({ pages, user, logoUrl, onSignOut: onLogoff, managerView }) => {
  const [selectedMenu, setSelectedMenu] = useState<'user' | 'switchView'>();
  const handleHide = useCallback(() => {
    setSelectedMenu(undefined);
  }, []);
  const clickOutsideRef = useClickOutside<HTMLDivElement>(handleHide);
  const menuItems = useMemo(
    () => [
      {
        element: <SignOutIcon />,
        label: <NamedContentEntryText container={'span'} refKey="menuSystem" subKey="signOut" />,
        action: () => onLogoff(),
      },
    ],
    [onLogoff],
  );

  const showManageViewButton = useMemo(() => user.role === 'manager', [user.role]);
  const navigate = useNavigate();

  const onSwitchView = (view: ViewOptions) => {
    if (view === ViewOptions.MANAGER && !managerView) {
      navigate('manager');
    }

    if (view === ViewOptions.USER && managerView) {
      navigate('..');
    }

    handleHide();
  };

  return (
    <div ref={clickOutsideRef}>
      <TopBarOuter>
        <TopBarInner>
          <LeftSection>
            <Logo src={logoUrl} alt="Logo" />
            <DesktopNavGroup>
              {showManageViewButton && (
                <ManagerViewContainer>
                  <ViewChipButton
                    type="button"
                    $isManagerView={!!managerView}
                    onClick={() => setSelectedMenu((prev) => (prev === 'switchView' ? undefined : 'switchView'))}
                  >
                    <ManagerViewButtonContainer>
                      <ManagerViewButtonText>
                        <ManagerViewButtonHighlight>
                          <NamedContentEntryText
                            refKey="menuSystem"
                            container="span"
                            subKey={managerView ? 'managerView' : 'userView'}
                          />
                        </ManagerViewButtonHighlight>
                      </ManagerViewButtonText>
                      <Icon icon={IconType.CHEVRON_DOWN} width={14} height={14} />
                    </ManagerViewButtonContainer>
                  </ViewChipButton>
                  <SwitchViewMenu selectedMenu={selectedMenu} onSwitch={onSwitchView} />
                </ManagerViewContainer>
              )}
              <NavLinks pages={pages} isManagerView={managerView} />
            </DesktopNavGroup>
          </LeftSection>
          <MobileSeparator />
          <ResponsiveMenuSection>
            <UtilityIconButton type="button" aria-label="Help">
              <HelpIcon />
            </UtilityIconButton>
            <UtilityIconButton type="button" aria-label="Notifications">
              <BellIcon />
            </UtilityIconButton>
            <User name={user.name} role={user.role} />
            <UserAvatar user={user} />
            <SettingsButton isOpen={selectedMenu === 'user'} setSelectedMenu={setSelectedMenu} />
            <SettingsMenu selectedMenu={selectedMenu} user={user} menuItems={menuItems} />
          </ResponsiveMenuSection>
          <MobileTopBarMenu pages={pages} managerView={managerView} onSignOut={onLogoff} />
        </TopBarInner>
      </TopBarOuter>
    </div>
  );
};

const TopBarOuter = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 999;
  width: 100%;
  background: ${({ theme }) => theme.colors.panelBackground};
  border-bottom: 1px solid #d5daee;
`;

const TopBarInner = styled.div`
  position: relative;
  width: 100%;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  min-height: 65px;

  @media (max-width: 768px) {
    min-height: 56px;
    padding: 10px 16px;
  }
`;

export const UserImg = styled.img`
  border-radius: 50%;
  width: 32px;
  height: 32px;
`;

const Logo = styled.img`
  max-height: 40px;
  max-width: 168px;
  object-fit: contain;
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 0;
`;

const DesktopNavGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileSeparator = styled.div`
  width: 1px;
  height: 32px;
  background: ${({ theme }) => theme.colors.tableBorder};
  display: none;

  @media (max-width: 768px) {
    display: block;
    margin-left: auto;
  }
`;

const ManagerViewContainer = styled.div`
  position: relative;

  @media (max-width: 768px) {
    display: none;
  }
`;

const ManagerViewButtonContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;

  svg path {
    stroke: #fff;
  }
`;

const ViewChipButton = styled.button.withConfig({
  shouldForwardProp: (prop) => prop !== '$isManagerView',
})<{ $isManagerView: boolean }>`
  border: none;
  border-radius: 999px;
  background: ${({ theme, $isManagerView }) => ($isManagerView ? theme.colors.managerPrimary : theme.colors.primary)};
  color: ${({ theme }) => theme.colors.primaryContrast};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 16px;
  min-height: 30px;
  cursor: pointer;
`;

const ManagerViewButtonText = styled.span`
  font-size: ${({ theme }) => theme.fontSizes.normal};
  line-height: 18px;
`;

const ManagerViewButtonHighlight = styled.span`
  font-weight: 500;
`;

const UtilityIconButton = styled.button`
  border: none;
  background: transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  color: ${({ theme }) => theme.colors.normal};
  cursor: pointer;

  svg {
    width: 18px;
    height: 18px;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const ResponsiveMenuSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-left: auto;
  position: relative;

  @media (max-width: 768px) {
    display: none;
  }
`;
